import { LIMB_MAX_HP, type HealthState, type LimbId, type LimbState } from "../combat/health.js";
import type { MoveMods } from "./movement.js";

/**
 * Hunger and thirst. Two meters, `energy` and `hydration`, 0..`NEEDS_MAX`, full
 * at spawn and draining at a FLAT rate nothing the player does changes — a
 * promise to plan around: a raid of ordinary length costs nothing. Hydration
 * empties at 26 min, energy at 32, and the body only complains below
 * `NEEDS_LOW` (a quarter), so thirst first bites at 19.5 min and hunger at 24 —
 * past every extract timer on a 25-minute raid.
 *
 * Under `NEEDS_LOW`, thirst SLOWS THE BODY (`THIRSTY_MOVE_MULT`) and hunger
 * slows RECOVERY (`HUNGRY_STAMINA_REGEN_MULT`), one rail each, riding the
 * ordinary `MoveMods` (`moveModsFromNeeds`). At ZERO either meter drains the
 * thorax (`STARVING_HP_PER_SEC`) — a death that takes minutes. Pure and
 * rng-free; scavs are never stepped, so their needs stay full.
 */

export interface Needs {
  energy: number;
  hydration: number;
}

/** Full meter, both needs. */
export const NEEDS_MAX = 100;

/** Energy lost per second. `NEEDS_MAX` over 32 minutes. */
export const ENERGY_DRAIN_PER_SEC = NEEDS_MAX / (32 * 60);
/** Hydration lost per second. `NEEDS_MAX` over 26 minutes — thirst bites first. */
export const HYDRATION_DRAIN_PER_SEC = NEEDS_MAX / (26 * 60);

/** Strictly below this the penalties apply. A quarter of the meter. */
export const NEEDS_LOW = 25;

/** Speed AND acceleration multiplier while thirsty (hydration < `NEEDS_LOW`). */
export const THIRSTY_MOVE_MULT = 0.9;
/** Stamina regen multiplier while hungry (energy < `NEEDS_LOW`). */
export const HUNGRY_STAMINA_REGEN_MULT = 0.5;

/** Thorax hp lost per second PER EMPTY METER. Both empty is twice this. */
export const STARVING_HP_PER_SEC = 0.5;

/** The identity, shared by reference so a fed body never allocates. */
const FED: MoveMods = { speedMult: 1, accelMult: 1 };

export function createFullNeeds(): Needs {
  return { energy: NEEDS_MAX, hydration: NEEDS_MAX };
}

/**
 * Advances both meters by `dt` seconds. Returns the SAME object when both are
 * already at zero, so a caller can skip by identity (as with `stepHealth`).
 * Never below zero.
 */
export function stepNeeds(n: Needs, dt: number): Needs {
  if (n.energy <= 0 && n.hydration <= 0) return n;
  const energy = n.energy - ENERGY_DRAIN_PER_SEC * dt;
  const hydration = n.hydration - HYDRATION_DRAIN_PER_SEC * dt;
  return { energy: energy > 0 ? energy : 0, hydration: hydration > 0 ? hydration : 0 };
}

/**
 * What hunger and thirst do to the body, as movement modifiers. A fed body gets
 * the two-field identity WITHOUT a `staminaRegenMult` key, so a player with no
 * needs to speak of produces byte-identical `MoveMods`.
 */
export function moveModsFromNeeds(n: Needs): MoveMods {
  const thirsty = n.hydration < NEEDS_LOW;
  const hungry = n.energy < NEEDS_LOW;
  if (!thirsty && !hungry) return FED;
  const out: MoveMods = {
    speedMult: thirsty ? THIRSTY_MOVE_MULT : 1,
    accelMult: thirsty ? THIRSTY_MOVE_MULT : 1,
  };
  if (hungry) out.staminaRegenMult = HUNGRY_STAMINA_REGEN_MULT;
  return out;
}

/**
 * What one unit of a consumable puts back — the `consume` block on a
 * `LootItemDef`. Per unit, clamped at `NEEDS_MAX`; 0 means the item does
 * nothing for that meter.
 */
export interface ConsumeDef {
  /** Seconds the eating or drinking takes. The server runs the clock. */
  useTimeSec: number;
  energy: number;
  hydration: number;
}

/**
 * The least a bite must put back, summed over both meters, to be worth the unit
 * — about twenty seconds of the slower drain. A threshold rather than equality:
 * the raid drains (step 4b) before it feeds (step 6), so a "full" body is
 * always a sliver under `NEEDS_MAX`, and equality would spend a slab of bacon
 * on a hundredth of a point.
 */
export const CONSUME_MIN_GAIN = 1;

/**
 * Applies one unit of `def` to `n`. Returns the SAME object when the bite would
 * put back less than `CONSUME_MIN_GAIN` — the server's rejection condition,
 * mirroring `resolveMedTarget` answering `null`. Never mutates.
 */
export function consumeGain(n: Needs, def: ConsumeDef): Needs {
  const energy = clampNeed(n.energy + def.energy);
  const hydration = clampNeed(n.hydration + def.hydration);
  if (energy - n.energy + (hydration - n.hydration) < CONSUME_MIN_GAIN) return n;
  return { energy, hydration };
}

function clampNeed(v: number): number {
  return v > NEEDS_MAX ? NEEDS_MAX : v < 0 ? 0 : v;
}

/**
 * Drains the thorax by `dt` seconds of starvation and dehydration. Returns the
 * SAME health object when nothing is empty or the body is dead. The THORAX only
 * — one fixed sink, and the thorax at zero is what `dead` means; `dead` is
 * recomputed with `applyHit`'s expression.
 */
export function stepStarvation(h: HealthState, n: Needs, dt: number): HealthState {
  if (h.dead) return h;
  const empty = (n.energy <= 0 ? 1 : 0) + (n.hydration <= 0 ? 1 : 0);
  if (empty === 0) return h;

  const limbs = cloneLimbs(h.limbs);
  const t = limbs.thorax;
  const next = t.hp - STARVING_HP_PER_SEC * empty * dt;
  t.hp = next > 0 ? next : 0;
  return { limbs, dead: limbs.head.hp <= 0 || limbs.thorax.hp <= 0 };
}

/** Deep copy of the limb record — `combat/health.ts` keeps its own private. */
function cloneLimbs(src: Record<LimbId, LimbState>): Record<LimbId, LimbState> {
  const out = {} as Record<LimbId, LimbState>;
  for (const id of Object.keys(LIMB_MAX_HP) as LimbId[]) {
    const l = src[id];
    out[id] = { hp: l.hp, bleeding: l.bleeding, fractured: l.fractured };
  }
  return out;
}
